import type { MenuDataItem, Settings as LayoutSettings } from "@ant-design/pro-components";
import { formatMessage } from "@umijs/max";
import { EXAM_ADMIN_PAGE_PATHS, EXAM_COMMON_PAGE_PATHS } from "@airange/shared/exam-routes";
import { hasMessage } from "@airange/shared/i18n-exam-admin";
import { LOGIN_PATH } from "@airange/shared/oidc-routes";
import { useEffect, useState, type ComponentType, type ReactNode } from "react";

import type { UserPreferenceSettings } from "../lib/auth-api";
import type { PublicSystemConfig } from "../lib/system-api";
import type { UserSession } from "../lib/auth-api";

export const loginPath = LOGIN_PATH;
export const changePasswordPath = EXAM_COMMON_PAGE_PATHS.changePassword;
export const dashboardPath = EXAM_ADMIN_PAGE_PATHS.dashboard;

export type NavThemePreference = "light" | "realDark" | "system";
export type AdminLayoutSettings = Omit<Partial<LayoutSettings>, "navTheme"> & {
  navTheme?: NavThemePreference;
};

export type InitialState = {
  settings: AdminLayoutSettings;
  currentUser: UserSession | null;
  publicConfig: PublicSystemConfig | null;
  localeVersion: string;
};

export type BreadcrumbCrumb = {
  path?: string;
  breadcrumbName?: string;
};

type BreadcrumbGroup = {
  label: string;
  matches: (pathname: string) => boolean;
};

export type AdminMenuDataItem = MenuDataItem & {
  children?: AdminMenuDataItem[];
};

type CanonicalBreadcrumbRule = {
  pattern: RegExp;
  listPath: string;
  listNameKey: string;
  detailNameKey: string;
  excludeFirstSegment?: Set<string>;
  showFirstSegmentCrumb?: boolean;
};

export type AdminShellIconName =
  | "AppstoreOutlined"
  | "HddOutlined"
  | "LockOutlined"
  | "LogoutOutlined"
  | "SettingOutlined"
  | "TeamOutlined"
  | "UserOutlined";

type AntdIconModule = typeof import("@ant-design/icons");
type IconComponent = ComponentType<Record<string, never>>;

const adminShellIconCache = new Map<AdminShellIconName, IconComponent>();
let adminShellIconsPromise: Promise<AntdIconModule> | null = null;

function loadAdminShellIcons() {
  if (!adminShellIconsPromise) {
    adminShellIconsPromise = import("@ant-design/icons");
  }
  return adminShellIconsPromise;
}

export function AdminShellIcon({ name }: { name: AdminShellIconName }) {
  const [Icon, setIcon] = useState<IconComponent | null>(() => adminShellIconCache.get(name) ?? null);

  useEffect(() => {
    const cachedIcon = adminShellIconCache.get(name);
    if (cachedIcon) {
      setIcon(() => cachedIcon);
      return;
    }

    let cancelled = false;
    loadAdminShellIcons().then((module) => {
      const nextIcon = module[name] as unknown as IconComponent | undefined;
      if (!nextIcon) {
        return;
      }
      adminShellIconCache.set(name, nextIcon);
      if (!cancelled) {
        setIcon(() => nextIcon);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [name]);

  return Icon ? <Icon /> : null;
}

export function buildAdminValidateMessages() {
  return {
    required: formatMessage({ id: "common.validation.required" }),
    string: {
      min: formatMessage({ id: "common.validation.stringMin" }),
      max: formatMessage({ id: "common.validation.stringMax" }),
    },
    number: {
      range: formatMessage({ id: "common.validation.numberRange" }),
    },
    types: {
      email: formatMessage({ id: "common.validation.types.email" }),
      number: formatMessage({ id: "common.validation.types.number" }),
      url: formatMessage({ id: "common.validation.types.url" }),
    },
  };
}

export function getBreadcrumbGroup(pathname: string): string | null {
  const groups: BreadcrumbGroup[] = [
    {
      label: formatMessage({ id: "admin.shell.routes.businessGroup" }),
      matches: (value) =>
        value.startsWith("/questions") ||
        value.startsWith("/papers") ||
        value.startsWith(EXAM_ADMIN_PAGE_PATHS.exams) ||
        value.startsWith("/review"),
    },
    {
      label: formatMessage({ id: "admin.shell.routes.organizationGroup" }),
      matches: (value) => value.startsWith("/users/") || value.startsWith("/system/custom-fields"),
    },
    {
      label: formatMessage({ id: "admin.shell.routes.resourceGroup" }),
      matches: (value) => value.startsWith("/system/assets") || value.startsWith("/docker/"),
    },
    {
      label: formatMessage({ id: "admin.shell.routes.platformGroup" }),
      matches: (value) =>
        value.startsWith("/system/settings") ||
        value.startsWith("/audit-logs") ||
        value.startsWith("/execution") ||
        value.startsWith("/submissions"),
    },
  ];
  const hit = groups.find((item) => item.matches(pathname));
  return hit?.label ?? null;
}

const canonicalBreadcrumbRules: CanonicalBreadcrumbRule[] = [
  {
    pattern: /^\/questions\/([^/]+)(?:\/|$)/,
    listPath: "/questions",
    listNameKey: "admin.shell.menu.questions",
    detailNameKey: "admin.shell.routes.questionDetail",
    excludeFirstSegment: new Set(["create-code", "create-practical", "create-attack-defense"]),
  },
  {
    pattern: /^\/papers\/([^/]+)(?:\/|$)/,
    listPath: "/papers",
    listNameKey: "admin.shell.menu.papers",
    detailNameKey: "admin.shell.routes.paperDetail",
    excludeFirstSegment: new Set(["create", "manual-picker"]),
  },
  {
    pattern: /^\/exams\/([^/]+)(?:\/|$)/,
    listPath: EXAM_ADMIN_PAGE_PATHS.exams,
    listNameKey: "admin.shell.menu.exams",
    detailNameKey: "admin.shell.routes.examDetail",
  },
  {
    pattern: /^\/docker\/instances\/([^/]+)(?:\/|$)/,
    listPath: "/docker/instances",
    listNameKey: "admin.shell.menu.dockerInstances",
    detailNameKey: "admin.shell.routes.dockerInstanceDetail",
  },
];

export function buildCanonicalBreadcrumbs(pathname: string, routers: BreadcrumbCrumb[]) {
  const prefixCrumbs = routers.filter((item) => item.path === "/" || item.path === "__group__");
  for (const rule of canonicalBreadcrumbRules) {
    const match = pathname.match(rule.pattern);
    const firstSegment = match?.[1] ? decodeURIComponent(match[1]) : null;
    if (!firstSegment) continue;
    if (rule.excludeFirstSegment?.has(firstSegment)) continue;

    const listCrumb: BreadcrumbCrumb = { path: rule.listPath, breadcrumbName: formatMessage({ id: rule.listNameKey }) };
    const detailCrumb: BreadcrumbCrumb = { path: pathname, breadcrumbName: formatMessage({ id: rule.detailNameKey }) };
    if (rule.showFirstSegmentCrumb === false) {
      return [...prefixCrumbs, listCrumb, detailCrumb];
    }
    const codeCrumb: BreadcrumbCrumb = { path: `${rule.listPath}/${firstSegment}`, breadcrumbName: firstSegment };
    return [...prefixCrumbs, listCrumb, codeCrumb, detailCrumb];
  }
  return routers;
}

export function disableMenuTooltip(items: AdminMenuDataItem[] = []): AdminMenuDataItem[] {
  return items.map((item) => {
    const next: AdminMenuDataItem = {
      ...item,
      disabledTooltip: true,
      children: item.children ? disableMenuTooltip(item.children) : item.children,
    };
    return next;
  });
}

export function translateMenuData(items: AdminMenuDataItem[] = []): AdminMenuDataItem[] {
  return items.map((item) => ({
    ...item,
    name: typeof item.name === "string" && hasMessage(item.name) ? formatMessage({ id: item.name }) : item.name,
    children: item.children ? translateMenuData(item.children) : item.children,
  }));
}

export function groupAdminMenuData(items: AdminMenuDataItem[] = []): AdminMenuDataItem[] {
  const byPath = new Map<string, AdminMenuDataItem>();
  items.forEach((item) => {
    if (item.path) {
      byPath.set(item.path, item);
    }
  });

  const used = new Set<string>();
  const pick = (path: string) => {
    const item = byPath.get(path);
    if (item?.path) {
      used.add(item.path);
    }
    return item;
  };

  const dashboard = pick(EXAM_ADMIN_PAGE_PATHS.dashboard);

  const businessChildren = [pick("/questions"), pick("/papers"), pick(EXAM_ADMIN_PAGE_PATHS.exams), pick("/review")].filter(
    (item): item is AdminMenuDataItem => Boolean(item),
  );
  const organizationChildren = [pick("/users/staff"), pick("/users/candidates"), pick("/system/custom-fields")].filter(
    (item): item is AdminMenuDataItem => Boolean(item),
  );
  const resourceChildren = [pick("/system/assets"), pick("/docker/images"), pick("/docker/instances")].filter(
    (item): item is AdminMenuDataItem => Boolean(item),
  );
  const platformChildren = [
    pick("/system/settings"),
    pick("/audit-logs/login"),
    pick("/audit-logs/operations"),
    pick("/execution"),
    pick("/submissions"),
  ].filter((item): item is AdminMenuDataItem => Boolean(item));

  const grouped: AdminMenuDataItem[] = [];
  if (dashboard) {
    grouped.push(dashboard);
  }
  if (businessChildren.length > 0) {
    grouped.push({ key: "menu-group-business", name: "admin.shell.routes.businessGroup", icon: <AdminShellIcon name="AppstoreOutlined" />, children: businessChildren });
  }
  if (organizationChildren.length > 0) {
    grouped.push({ key: "menu-group-organization", name: "admin.shell.routes.organizationGroup", icon: <AdminShellIcon name="TeamOutlined" />, children: organizationChildren });
  }
  if (resourceChildren.length > 0) {
    grouped.push({ key: "menu-group-resource", name: "admin.shell.routes.resourceGroup", icon: <AdminShellIcon name="HddOutlined" />, children: resourceChildren });
  }
  if (platformChildren.length > 0) {
    grouped.push({ key: "menu-group-platform", name: "admin.shell.routes.platformGroup", icon: <AdminShellIcon name="SettingOutlined" />, children: platformChildren });
  }

  const leftovers = items.filter((item) => !item.path || !used.has(item.path));
  return [...grouped, ...leftovers];
}

export function getSystemPrefersDark() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export function resolveNavTheme(navTheme: NavThemePreference, systemPrefersDark: boolean): "light" | "realDark" {
  if (navTheme === "system") {
    return systemPrefersDark ? "realDark" : "light";
  }
  return navTheme === "realDark" ? "realDark" : "light";
}

export function toPreferenceSettings(settings: Partial<LayoutSettings>): UserPreferenceSettings {
  const navThemeValue = settings.navTheme === "realDark" || settings.navTheme === "light" ? settings.navTheme : undefined;
  const layoutValue = settings.layout === "side" || settings.layout === "top" || settings.layout === "mix" ? settings.layout : undefined;
  const contentWidthValue = settings.contentWidth === "Fluid" || settings.contentWidth === "Fixed" ? settings.contentWidth : undefined;
  return {
    navTheme: navThemeValue,
    layout: layoutValue,
    contentWidth: contentWidthValue,
    fixedHeader: typeof settings.fixedHeader === "boolean" ? settings.fixedHeader : undefined,
    fixSiderbar: typeof settings.fixSiderbar === "boolean" ? settings.fixSiderbar : undefined,
    splitMenus: typeof settings.splitMenus === "boolean" ? settings.splitMenus : undefined,
    colorPrimary: typeof settings.colorPrimary === "string" ? settings.colorPrimary : undefined,
    colorWeak: typeof settings.colorWeak === "boolean" ? settings.colorWeak : undefined,
  };
}
